import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Client } from '../types/entities';

interface ClientInfoModalProps {
  open: boolean;
  onClose: () => void;
  client: Client | null;
}

const ClientInfoModal: React.FC<ClientInfoModalProps> = ({
  open,
  onClose,
  client,
}) => {
  const { t } = useTranslation();
  if (!client) return null;

  const getLastAppointmentDate = (): string => {
    if (!client.appointments || client.appointments.length === 0) {
      return t('clients.noAppointmentsYet');
    }
    
    const sortedAppointments = client.appointments
      .filter(apt => apt.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    if (sortedAppointments.length === 0) {
      return t('clients.noAppointmentsYet');
    }
    
    const lastAppointment = sortedAppointments[0];
    // Handle UTC date from database and convert to local timezone
    const localDate = new Date(lastAppointment.date);
    
    // Format as dd.mm.yyyy (date only, no time)
    const day = String(localDate.getDate()).padStart(2, '0');
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const year = localDate.getFullYear();
    
    return `${day}.${month}.${year}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('clients.clientInfo')}</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="h6" gutterBottom>
            {client.name}
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          {client.phone_number && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('common.phone')}
              </Typography>
              <Typography variant="body1">
                {client.phone_number}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('common.notes')}
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {client.notes || t('clients.noNotes')}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('clients.lastAppointment')}
            </Typography>
            <Typography variant="body1">
              {getLastAppointmentDate()}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ClientInfoModal;
